import { ApiErrorSchema, type ApiError } from "#shared/schemas/health.ts";
import { coyLog } from "@web/lib/coyLog.ts";
import { recordLastRequest } from "@web/features/diagnostics/lastRequest.ts";
import { useUiStore } from "@web/stores/ui.ts";

/**
 * Typed fetch against our API. Failures become Error with the server's message —
 * never a silent console.warn (that was a real data-loss path in v0).
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly errorId: string | undefined;

  constructor(status: number, message: string, code?: string, errorId?: string) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
    this.errorId = errorId;
  }
}

/** Clerk `getToken` bridge — set from a SignedIn tree so every `api()` call auths. */
let tokenGetter: (() => Promise<string | null>) | null = null;

export function setApiTokenGetter(getter: (() => Promise<string | null>) | null): void {
  tokenGetter = getter;
}

function debugEnabled(): boolean {
  try {
    return useUiStore.getState().debugMode;
  } catch {
    return false;
  }
}

function pathOnly(path: string): string {
  try {
    return path.startsWith("http") ? new URL(path).pathname : path.split("?")[0] ?? path;
  } catch {
    return path;
  }
}

function readStringField(body: unknown, key: string): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const record = body as Record<string, unknown>;
  const value = record[key];
  return typeof value === "string" ? value : undefined;
}

export async function api<T>(
  path: string,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const { json, headers, ...rest } = init ?? {};
  const method = (rest.method ?? (json !== undefined ? "POST" : "GET")).toUpperCase();
  const token = tokenGetter ? await tokenGetter() : null;
  const started = performance.now();
  const debug = debugEnabled();

  let res: Response;
  try {
    res = await fetch(path, {
      ...rest,
      method,
      headers: {
        ...(json !== undefined ? { "content-type": "application/json" } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
    });
  } catch (err) {
    const ms = Math.round(performance.now() - started);
    recordLastRequest({
      method,
      path: pathOnly(path),
      status: "network",
      ms,
      at: Date.now(),
    });
    if (debug) {
      coyLog.error(`API ${method} ${pathOnly(path)} network fail ${ms}ms`, err);
    }
    throw new ApiRequestError(
      0,
      "Can't reach CoyKoi's server. It may be restarting — try again in a minute.",
      "API_UNREACHABLE",
    );
  }

  const ms = Math.round(performance.now() - started);

  if (res.status === 204) {
    recordLastRequest({
      method,
      path: pathOnly(path),
      status: 204,
      ms,
      at: Date.now(),
    });
    if (debug) coyLog.info(`API ${method} ${pathOnly(path)} → 204 ${ms}ms`);
    return undefined as T;
  }

  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const parsed = ApiErrorSchema.safeParse(body);
    const envelope: ApiError | null = parsed.success ? parsed.data : null;
    const legacyMessage = readStringField(body, "error");
    const legacyCode = readStringField(body, "code");
    const message =
      envelope?.message ?? legacyMessage ?? `Request failed (${res.status})`;
    const code = envelope?.code ?? legacyCode;
    const errorId = envelope?.errorId;

    recordLastRequest({
      method,
      path: pathOnly(path),
      status: res.status,
      ms,
      errorId,
      at: Date.now(),
    });
    if (debug) {
      coyLog.error(
        `API ${method} ${pathOnly(path)} → ${res.status} ${ms}ms` +
          (errorId ? ` errorId=${errorId}` : "") +
          (code ? ` code=${code}` : ""),
        { message, body },
      );
    }
    throw new ApiRequestError(res.status, message, code, errorId);
  }

  recordLastRequest({
    method,
    path: pathOnly(path),
    status: res.status,
    ms,
    at: Date.now(),
  });
  if (debug) {
    coyLog.info(`API ${method} ${pathOnly(path)} → ${res.status} ${ms}ms`);
  }

  return body as T;
}
